import { useMemo } from "react";

import { faArrowDownWideShort } from "@fortawesome/pro-solid-svg-icons";
import { Column } from "@tanstack/react-table";
import { isString, uniq } from "lodash-es";

import {
  ColumnFilter,
  ColumnMultiValueFilter,
} from "src/base-components/ColumnFilter";
import { Icon } from "src/base-components/Icon";
import { useFilter } from "src/base-components/tableColumnFilters/useFilters";
import {
  EntityMapping,
  FeatureQueryStatus,
} from "src/clients/features-control/api";
import { Feature, Query } from "src/featureCatalogue/utils";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";

type FilterableRow = Feature | Query;

type GenericColumnType<T> = Column<T, unknown>;

export const StatusFilter = <T extends FilterableRow>({
  column,
  property,
}: {
  column?: GenericColumnType<T>;
  property?: string;
}): React.ReactElement => {
  const featureLifecycleEnabled = isFeatureFlagEnabled(
    FEATURE_FLAGS.featureLifecycle,
  );
  const effectiveProperty = property ?? "status";
  const {
    filters: urlFilters,
    update,
    apply,
    reset,
    isFiltering,
    isApplied,
  } = useFilter(effectiveProperty, [
    { key: effectiveProperty, operator: "eq", values: [] },
  ]);
  const filter = urlFilters[0];

  if (!featureLifecycleEnabled && !column) return <></>;

  if (!featureLifecycleEnabled) {
    return (
      <ColumnFilter
        dataLoc={`${effectiveProperty}-filter`}
        description="Status filter:"
        elements={[
          { key: FeatureQueryStatus.ACTIVE, value: "active" },
          { key: FeatureQueryStatus.INACTIVE, value: "inactive" },
        ]}
        isFiltering={column!.getIsFiltered()}
        selected={column!.getFilterValue() as string | undefined}
        onResetRequest={() => column!.setFilterValue(undefined)}
        onSelect={column!.setFilterValue}
      />
    );
  }

  return (
    <ColumnMultiValueFilter
      dataLoc={`${effectiveProperty}-filter`}
      defaultValue={undefined}
      headline="Status filter:"
      isApplied={isApplied()}
      isFiltering={isFiltering({ key: effectiveProperty })}
      options={[
        { key: FeatureQueryStatus.ACTIVE, value: "active" },
        { key: FeatureQueryStatus.INACTIVE, value: "inactive" },
      ]}
      values={filter.values}
      onApply={apply}
      onChange={(values) => update([{ ...filter, values }])}
      onResetRequest={() => reset()}
    />
  );
};

export const EventTypeFilter = <T extends FilterableRow>({
  column,
  options,
}: {
  column?: GenericColumnType<T>;
  options?: string[];
}): React.ReactElement => {
  const featureLifecycleEnabled = isFeatureFlagEnabled(
    FEATURE_FLAGS.featureLifecycle,
  );
  const {
    filters: urlFilters,
    update,
    apply,
    reset,
    isFiltering,
    isApplied,
  } = useFilter("event", [{ key: "event", operator: "eq", values: [] }]);
  const filter = urlFilters[0];

  const facetedValues = column?.getFacetedUniqueValues();
  const columnElements = useMemo(
    () =>
      facetedValues
        ? Array.from(facetedValues.keys()).map((key) => ({
            key: key,
            value: key,
          }))
        : [],
    [facetedValues],
  );

  if (!featureLifecycleEnabled && !column) return <></>;

  const elements = featureLifecycleEnabled
    ? (options ?? []).map((o) => ({ key: o, value: o }))
    : columnElements;

  if (!featureLifecycleEnabled) {
    return (
      <ColumnFilter
        dataLoc="event-filter"
        description="Event type filter:"
        elements={elements}
        isFiltering={column!.getIsFiltered()}
        selected={column!.getFilterValue() as string | undefined}
        onResetRequest={() => column!.setFilterValue(undefined)}
        onSelect={column!.setFilterValue}
      />
    );
  }

  return (
    <ColumnMultiValueFilter
      dataLoc="event-filter"
      defaultValue={undefined}
      headline="Event type filter:"
      isApplied={isApplied()}
      isFiltering={isFiltering({ key: "event" })}
      options={elements}
      values={filter.values}
      onApply={apply}
      onChange={(values) => update([{ ...filter, values }])}
      onResetRequest={() => reset()}
    />
  );
};

export const EntityFilter = <T extends FilterableRow>({
  column,
  type,
  options,
  property,
}: {
  column?: GenericColumnType<T>;
  type?: "feature" | "query";
  options?: string[];
  property?: string;
}): React.ReactElement => {
  const featureLifecycleEnabled = isFeatureFlagEnabled(
    FEATURE_FLAGS.featureLifecycle,
  );
  const effectiveProperty = property ?? "entity_types";
  const {
    filters: urlFilters,
    update,
    apply,
    reset,
    isFiltering,
    isApplied,
  } = useFilter(effectiveProperty, [
    { key: effectiveProperty, operator: "eq", values: [] },
  ]);
  const filter = urlFilters[0];

  const facetedRowModel = column?.getFacetedRowModel();
  const columnElements = useMemo(() => {
    if (!facetedRowModel) return [];
    let entityNames: string[];
    if (type === "query") {
      entityNames = uniq(
        facetedRowModel.flatRows
          .flatMap((row) =>
            (row.original as Query)?.columns?.entities?.map(
              (entity: EntityMapping) => entity.entity_name,
            ),
          )
          .filter(isString),
      );
    } else {
      entityNames = uniq(
        facetedRowModel.flatRows
          .flatMap((row) => (row.original as Feature)?.entity_types)
          .filter(isString),
      );
    }
    return entityNames.map((entity) => ({ key: entity, value: entity }));
  }, [facetedRowModel, type]);

  if (!featureLifecycleEnabled && !column) return <></>;

  const elements = featureLifecycleEnabled
    ? (options ?? []).map((o) => ({ key: o, value: o }))
    : columnElements;

  if (!featureLifecycleEnabled) {
    return (
      <ColumnFilter
        dataLoc={`${effectiveProperty}-filter`}
        description="Entity type filter:"
        elements={elements}
        isFiltering={column!.getIsFiltered()}
        selected={column!.getFilterValue() as string | undefined}
        onResetRequest={() => column!.setFilterValue(undefined)}
        onSelect={column!.setFilterValue}
      />
    );
  }

  return (
    <ColumnMultiValueFilter
      dataLoc={`${effectiveProperty}-filter`}
      defaultValue={undefined}
      headline="Entity type filter:"
      isApplied={isApplied()}
      isFiltering={isFiltering({ key: effectiveProperty })}
      options={elements}
      values={filter.values}
      onApply={apply}
      onChange={(values) => update([{ ...filter, values }])}
      onResetRequest={() => reset()}
    />
  );
};

export const SortActionButton = <T,>({
  column,
}: {
  column: GenericColumnType<T>;
}) => {
  const isSorted = column.getIsSorted();

  return (
    <button
      className="flex items-center"
      type="button"
      onClick={() => {
        let nextDesc: boolean | undefined;
        if (isSorted === "asc") {
          nextDesc = true;
        } else if (isSorted === "desc") {
          nextDesc = false;
        }
        column.clearSorting();
        column.toggleSorting(nextDesc);
      }}
    >
      <Icon
        color={
          isSorted === "desc"
            ? "text-indigo-500"
            : "text-gray-500 hover:text-gray-900"
        }
        icon={faArrowDownWideShort}
        size="xs"
      />
    </button>
  );
};
